<template>
  <div class="custom-checkbox-wrapper d-flex align-items-center column-gap-2">
    <input
      type="checkbox"
      :id="fieldId"
      :checked="isChecked"
      @change="toggleCheck"
      style="display: none"
    />

    <div
      class="custom-checkbox cursor-pointer position-relative"
      :class="{ checked: isChecked, disabled }"
      @click="toggleCheck"
    >
      <IconCheckbox v-if="!isChecked" />
      <IconCheckboxChecked v-if="isChecked" />
      <IconCheckMark
        :width="10"
        :height="10"
        class="icon-check"
        v-if="isChecked"
      />
    </div>

    <label v-if="label" :for="fieldId" class="w-100 cursor-pointer">
      {{ label }}
    </label>
  </div>
</template>

<script setup>
import { v4 as uuid } from "uuid";
import { objectsEqual } from "@/utils/utils.js";
import IconCheckbox from "@/components/icons/IconCheckbox.vue";

const props = defineProps({
  fieldId: {
    type: [String, Number],
    default: () => uuid(),
  },
  label: {
    type: String,
    default: null,
  },
  modelValue: {
    type: [Boolean, String, Number, Object, Array],
    default: false,
  },
  value: {
    type: [String, Number, Object, Boolean],
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: true, // multiple selection is enabled by default
  },
});

const emit = defineEmits(["update:modelValue"]);

const isChecked = computed(() => {
  if (Array.isArray(props.modelValue)) {
    if (typeof props.value === "object") {
      return props.modelValue.some((item) => objectsEqual(item, props.value));
    }
    return props.modelValue.includes(props.value);
  }
  return props.modelValue === props.value;
});

const toggleCheck = () => {
  if (props.disabled) return;

  if (props.multiple) {
    if (Array.isArray(props.modelValue)) {
      const newValue = [...props.modelValue];
      const index =
        typeof props.value === "object"
          ? newValue.findIndex((item) => objectsEqual(item, props.value))
          : newValue.indexOf(props.value);

      if (index > -1) {
        newValue.splice(index, 1);
      } else {
        newValue.push(props.value);
      }
      emit("update:modelValue", newValue); // Emit the updated array
      return;
    }
    emit("update:modelValue", isChecked.value ? null : props.value); // For single value, toggle between null and the value
  } else {
    if (Array.isArray(props.modelValue)) {
      if (!props.modelValue.length || props.modelValue.includes(props.value)) {
        emit("update:modelValue", [props.value]); // Allow only this checkbox to be selected
      }
      return;
    }
    // Toggle single value for non-array modelValue
    emit("update:modelValue", isChecked.value ? null : props.value);
  }
};
</script>

<style scoped lang="scss">
.custom-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.checked {
    :deep(.icon-check) {
      position: absolute;
    }
  }
}
</style>
